import React from 'react';
import {
  FaStar, FaCheck, FaCertificate, FaDollarSign, FaGlobe, FaPhone, FaEnvelope, FaMapMarkerAlt, FaBriefcase,
} from 'react-icons/fa';
import { ServiceBadges, ProDisclosures, ReportProfessionalButton } from './ProSafeguardsDisplay';

function Chips({ title, items, className, Icon }) {
  if (!items?.length) return null;
  return (
    <div className="mb-6">
      <h3 className="font-bold text-gray-800 mb-2">{title}</h3>
      <div className="flex flex-wrap gap-2">
        {items.map((item, i) => (
          <span key={i} className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${className}`}>
            {Icon && <Icon className="mr-1" />}{item}
          </span>
        ))}
      </div>
    </div>
  );
}

function Stat({ show, bg, color, Icon, value, label }) {
  if (!show) return null;
  return (
    <div className={`text-center p-3 rounded-lg ${bg}`}>
      <div className={`flex items-center justify-center mb-1 ${color}`}>
        <Icon className="mr-1" /><span className="text-xl font-bold">{value}</span>
      </div>
      <p className="text-xs text-gray-600">{label}</p>
    </div>
  );
}

function ContactRow({ Icon, children }) {
  return <div className="flex items-center text-gray-600"><Icon className="mr-3 text-gray-400" />{children}</div>;
}

export function ProfileDetails({ professional: p, onReported }) {
  const place = p.location || (p.city ? `${p.city}${p.state ? `, ${p.state}` : ''}` : null);
  return (
    <>
      <div className="grid grid-cols-3 gap-4 mb-6">
        <Stat show={!!p.rating} bg="bg-amber-50" color="text-amber-500" Icon={FaStar} value={p.rating?.toFixed(1)} label={`${p.review_count || 0} reviews`} />
        <Stat show={!!p.years_experience} bg="bg-blue-50" color="text-blue-600" Icon={FaBriefcase} value={`${p.years_experience}+`} label="Years Experience" />
        <Stat show={!!p.hourly_rate} bg="bg-green-50" color="text-green-600" Icon={FaDollarSign} value={p.hourly_rate} label="Per Hour" />
      </div>
      {p.offers_free_consultation && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-6 flex items-center">
          <FaCheck className="text-green-600 mr-2" />
          <span className="text-green-800 font-medium">Offers Free Initial Consultation</span>
        </div>
      )}
      <ServiceBadges professional={p} />
      <ProDisclosures professional={p} />
      <div className="mb-6"><ReportProfessionalButton professional={p} onReported={onReported} /></div>
      {p.bio && (
        <div className="mb-6">
          <h3 className="font-bold text-gray-800 mb-2">About</h3>
          <p className="text-gray-600 whitespace-pre-line">{p.bio}</p>
        </div>
      )}
      <Chips title="Specialties" items={p.specialties} className="bg-blue-100 text-blue-800" />
      <Chips title="Certifications" items={p.certifications} className="bg-amber-100 text-amber-800" Icon={FaCertificate} />
      <div className="mb-6">
        <h3 className="font-bold text-gray-800 mb-2">Contact</h3>
        <div className="space-y-2">
          {place && <ContactRow Icon={FaMapMarkerAlt}>{place}</ContactRow>}
          {p.email && <ContactRow Icon={FaEnvelope}><a href={`mailto:${p.email}`} className="text-blue-600 hover:underline">{p.email}</a></ContactRow>}
          {p.phone && <ContactRow Icon={FaPhone}><a href={`tel:${p.phone}`} className="text-blue-600 hover:underline">{p.phone}</a></ContactRow>}
          {p.website && (
            <ContactRow Icon={FaGlobe}>
              <a href={p.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline break-all">{p.website}</a>
            </ContactRow>
          )}
        </div>
      </div>
    </>
  );
}
